import { computed, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { DataHeaderAppliedFilter, DataHeaderFilter, FilterChangedDetection } from '../data-table';
import { ActionButton, ActionMenu, DataListColumn } from '../data-table/data-table.interface';
import { BasicPaginationComponent } from './basic-pagination.abstract.component';

export abstract class BaseDataTableComponent<T, K extends string> extends BasicPaginationComponent<T> {
  public abstract columns: Signal<DataListColumn<K>[]>;
  public abstract topButtons: Signal<ActionButton[]>;
  public abstract itemActions: Signal<ActionButton[]>;
  public abstract topMenu: Signal<ActionMenu | null>;
  appliedFilter = signal<Record<string, string | string[]> | null>(null);
  filters = signal<DataHeaderFilter<K>[]>([]);
  currentFilterView = signal<string | null>(null);

  constructor() {
    super();
    this.loading
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((loading) => this.isLoading.set(loading)),
      )
      .subscribe();
  }

  mappedFilters: Signal<DataHeaderAppliedFilter<K>[]> = computed(() => {
    const filters = this.appliedFilter();

    if (!filters) return [];

    return Object.entries(filters)
      .map(([key, value]) => {
        const values = Array.isArray(value) ? value : [value];
        const entries = values
          .filter((raw) => raw != null)
          .map((raw) => ({
            raw,
            display: this.getDisplayForEntry(key as K, raw),
          }));

        if (entries.length === 0) return null;

        return {
          key,
          label: this.getColumnLabelForKey(key),
          entries,
        };
      })
      .filter((f): f is DataHeaderAppliedFilter<K> => f !== null);
  });

  getColumnLabelForKey(key: string) {
    return this.filters().find((filter) => filter.column === key)?.label;
  }

  onFilterViewChanged(view: string) {
    this.currentFilterView.set(view);
  }

  doRemoveFilter(event: { key: string; value: string }) {
    const currentFilters = this.appliedFilter();
    if (!currentFilters) return;

    const updatedFilters = { ...currentFilters };

    const currentValue = updatedFilters[event.key];
    if (!currentValue) return;

    if (Array.isArray(currentValue)) {
      const filteredValues = currentValue.filter((val) => val !== event.value);

      if (filteredValues.length > 0) {
        updatedFilters[event.key] = filteredValues;
      } else {
        delete updatedFilters[event.key];
      }
    } else {
      if (currentValue === event.value) {
        delete updatedFilters[event.key];
      }
    }
    this.appliedFilter.set(updatedFilters);
    this.pagination().filters = updatedFilters;
    this.doRefresh();
  }

  doClearFilter() {
    this.appliedFilter.set(null);
    this.pagination().filters = undefined;
    this.doRefresh();
  }

  filterChanged(event: FilterChangedDetection) {
    this.appliedFilter.set(event.filter);
    this.pagination().filters = event.filter;
  }

  abstract getDisplayForEntry(key: K, rawValue: string): string;
  abstract onItemClicked(item: T, auxClick?: boolean): void;
}
